import { Dialog, DialogTitle, IconButton, Stack } from '@mui/material';
import Close from '@mui/icons-material/Close';
import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import useLocales from '../hooks/useLocales';

interface GenericDialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  disableBackdropClick?: boolean;
  disableEscapeKeyDown?: boolean;
  /** Disables the header's X button — pair with disableBackdropClick/disableEscapeKeyDown
   * to fully block dismissal while a request triggered by this dialog is in flight. */
  disableCloseButton?: boolean;
  headerActions?: ReactNode;
  paperSx?: SxProps<Theme>;
}

export function GenericDialog({
  open,
  onClose,
  title,
  children,
  maxWidth = 'sm',
  fullWidth = false,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  disableCloseButton = false,
  headerActions,
  paperSx
}: GenericDialogProps) {
  const { translate } = useLocales();
  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (disableEscapeKeyDown && reason === 'escapeKeyDown') return;
        if (disableBackdropClick && reason === 'backdropClick') return;
        onClose();
      }}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      slotProps={{
        paper: {
          sx: {
            width: fullWidth ? '100%' : 'auto',
            ...((paperSx as object) ?? {})
          }
        }
      }}
    >
      <DialogTitle>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          {title}
          <Stack direction="row" sx={{ flexShrink: 0, gap: 0.5 }}>
            {headerActions}
            <IconButton
              aria-label={translate('common.close')}
              onClick={onClose}
              disabled={disableCloseButton}
              size="small"
            >
              <Close />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      {children}
    </Dialog>
  );
}
